import React, { createRef } from 'react';

import { Col } from 'react-bootstrap';
import { Formik } from 'formik';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { CLOSE_POPUP } from 'app/modules/MyAccount/pages/PaymentMethods/utils';
import { URLS } from 'app/config/urls';
import CreditCards from 'app/modules/MyAccount/pages/PaymentMethods/components/CreditCards';
import Divider from 'app/components/Divider';
import { PACK_TYPE_NUMBER } from 'app/utils/constants';
import FormCard from 'app/components/FormCard';
import MyAccountLayout from 'app/modules/MyAccount/components/MyAccountLayout';
import Notification from 'app/components/Notification';
import PayPal from 'app/modules/MyAccount/pages/PaymentMethods/components/PayPal';
import Popup from 'app/components/Popup';
import usePaymentMethodsContext from 'app/modules/MyAccount/pages/PaymentMethods/context/PaymentMethodsContext';
import LoadingState from 'app/modules/MyAccount/pages/PaymentMethods/components/LoadingState/LoadingState';
import SubscriptionSection from 'app/modules/MyAccount/pages/PaymentMethods/components/SubscriptionSection/SubscriptionSection';

import 'app/modules/MyAccount/pages/PaymentMethods/PaymentMethods.scss';

export default function PaymentMethods() {
  const intl = useIntl();
  const { user } = useSelector((state) => state.auth);
  const form = createRef();
  const container = createRef();

  const showB2BSubscriptionNotification =
    user?.attributes.packId === PACK_TYPE_NUMBER.B2B;

  const {
    loading,
    popupIsOpen,
    userSubscriptions,
    hasUserSubscriptions,
    hasNextExpirationSubscriptions,
    hasCreditCards,
    hasPaycomet,
    hasPayPal,
    popupContent: { popupHeader, popupBody, popupFooter },
    handleTogglePopup
  } = usePaymentMethodsContext();

  const showOrdersNotification = !hasCreditCards && !hasPaycomet && !hasPayPal;

  if (loading) {
    return (
      <MyAccountLayout currentPagePath={URLS.myAccountPaymentMethods}>
        <LoadingState containerMaxHeight={container?.current?.clientHeight} />
      </MyAccountLayout>
    );
  }

  return (
    <MyAccountLayout currentPagePath={URLS.myAccountPaymentMethods}>
      <Col md={8} className="bb-payment-methods-bigbuy-page">
        <FormCard
          title={intl.formatMessage({
            id: 'controlpanel.payment_methods.title'
          })}
        >
          <div
            className="bb-payment-methods-bigbuy-page__content"
            ref={container}
          >
            <div className="bb-payment-methods-bigbuy-page__content__subscriptions">
              <div className="bb-payment-methods-bigbuy-page__content__subscriptions__header">
                <p className="medium big bb-payment-methods-bigbuy-page__content__subscriptions__header__title">
                  {intl.formatMessage({
                    id: 'controlpanel.payment_methods.subscription.title'
                  })}
                </p>
                <p className="bb-payment-methods-bigbuy-page__content__subscriptions__header__subtitle">
                  {intl.formatMessage({
                    id: 'controlpanel.payment_methods.subscription.subtitle'
                  })}
                </p>
              </div>
              <SubscriptionSection
                showB2BSubscriptionNotification={
                  showB2BSubscriptionNotification
                }
                hasUserSubscriptions={hasUserSubscriptions}
                hasNextExpirationSubscriptions={hasNextExpirationSubscriptions}
                userSubscriptions={userSubscriptions}
              />
            </div>
            <Divider />
            <div className="bb-payment-methods-bigbuy-page__content__orders">
              <div className="bb-payment-methods-bigbuy-page__content__orders__header">
                <p className="medium big bb-payment-methods-bigbuy-page__content__orders__header__title">
                  {intl.formatMessage({
                    id: 'controlpanel.breadcrumbs.orders'
                  })}
                </p>
                <p className="bb-payment-methods-bigbuy-page__content__orders__header__subtitle">
                  {intl.formatMessage({
                    id: 'controlpanel.payment_methods.orders.subtitle_1'
                  })}
                </p>
                <p className="bb-payment-methods-bigbuy-page__content__orders__header__subtitle">
                  {intl.formatMessage({
                    id: 'controlpanel.payment_methods.orders.subtitle_2'
                  })}
                </p>
              </div>
              {showOrdersNotification && (
                <div className="bb-payment-methods-notification">
                  <Notification
                    header={intl.formatMessage({
                      id: 'controlpanel.payment_methods.orders.notification.title'
                    })}
                    content={intl.formatMessage({
                      id: 'controlpanel.payment_methods.orders.notification.subtitle'
                    })}
                    variant="info"
                    button={null}
                  />
                </div>
              )}
              {(hasCreditCards || hasPaycomet) && (
                <>
                  <CreditCards />
                  <Divider
                    type="dash"
                    className="bb-payment-methods-bigbuy-page__divider"
                  />
                </>
              )}
              <PayPal />
            </div>
          </div>
        </FormCard>
      </Col>
      <Formik
        initialValues={{ setAsDefault: false }}
        onSubmit={() => form.current.dispatchEvent(new Event('submit'))}
      >
        <Popup
          show={popupIsOpen}
          handleClose={() => handleTogglePopup(CLOSE_POPUP)}
          header={popupHeader()}
          body={popupBody({ ref: form })}
          footer={popupFooter({
            onClose: () => handleTogglePopup(CLOSE_POPUP)
          })}
        />
      </Formik>
    </MyAccountLayout>
  );
}
